(() => {
  const initialized = new WeakSet();

  function formatCounter(el, value) {
    const type = el.dataset.format;
    if (type === 'rating') return value.toFixed(1) + ' ★';
    if (type === 'million-plus') return (value / 1000000).toFixed(2).replace(/0+$/, '').replace(/\.$/, '') + 'M+';
    if (type === 'compact-plus') {
      if (value >= 1000000) return (value / 1000000).toFixed(1).replace(/\.0$/, '') + 'M+';
      if (value >= 1000) return Math.round(value / 1000) + 'K+';
      return Math.round(value).toLocaleString() + '+';
    }
    return Math.round(value).toLocaleString();
  }

  function animateCounter(el) {
    if (el.dataset.animated === '1') return;
    el.dataset.animated = '1';
    const target = parseFloat(el.dataset.value || '0');
    const duration = 1500;
    let start = null;
    const parent = el.closest('.ck47-proof-stat');

    function tick(ts) {
      if (!start) start = ts;
      const progress = Math.min((ts - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      el.textContent = formatCounter(el, target * eased);
      if (progress < 1) requestAnimationFrame(tick);
      else {
        el.textContent = formatCounter(el, target);
        if (parent) parent.classList.add('is-counted');
      }
    }
    requestAnimationFrame(tick);
  }

  function smsHref(root) {
    const number = root.dataset.smsNumber || '3158285833';
    const keyword = root.dataset.smsKeyword || 'CK';
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    return isIOS
      ? `sms:+1${number}&body=${encodeURIComponent(keyword)}`
      : `sms:+1${number}?body=${encodeURIComponent(keyword)}`;
  }

  function setLiveStatus(root, data) {
    const el = root.querySelector('[data-ck47-live-status]');
    const text = root.querySelector('[data-ck47-status-text]');
    if (!el || !text) return;
    el.classList.remove('is-live', 'is-offline');

    const magic = !!data.magic;
    const pokemon = !!data.pokemon;
    if (magic || pokemon) {
      el.classList.add('is-live');
      if (magic && pokemon) {
        text.textContent = 'BOTH LIVE';
        el.href = root.querySelector('[data-channel="magic"]') ? '#'+root.querySelector('[id*="ck47-live-"]').id : '#';
      } else if (magic) {
        text.textContent = 'MAGIC LIVE';
        el.href = root.dataset.magicUrl;
      } else {
        text.textContent = 'POKÉMON LIVE';
        el.href = root.dataset.pokemonUrl;
      }
    } else {
      el.classList.add('is-offline');
      text.textContent = 'LIVE 24/5';
    }
  }

  async function refreshLiveStatus(root) {
    const endpoint = (root.dataset.liveEndpoint || '').trim();
    if (!endpoint) return;
    try {
      const res = await fetch(endpoint, { cache: 'no-store', credentials: 'same-origin' });
      if (!res.ok) throw new Error('Live status unavailable');
      setLiveStatus(root, await res.json());
    } catch (e) {
      const text = root.querySelector('[data-ck47-status-text]');
      if (text) text.textContent = 'LIVE 24/5';
    }
  }

  function init(root) {
    if (!root || initialized.has(root)) return;
    initialized.add(root);

    if (root.dataset.hideThemeChrome === 'true') {
      document.body.classList.add('ck47-page-active');
    }

    root.querySelectorAll('[data-ck47-sms-link]').forEach(link => {
      link.href = smsHref(root);
    });

    const channelData = {
      magic: {
        label: 'Selected: Magic',
        kicker: 'Magic: The Gathering • Whatnot',
        title: '@cardking47_tcg',
        copy: 'One of the largest MTG communities on Whatnot, with Surprise Sets, breaks, sealed product and live auctions built for collectors.',
        tags: ['Surprise Sets', 'Breaks', 'Live Auctions'],
        href: root.dataset.magicUrl,
        button: 'Open Magic Shows ↗'
      },
      pokemon: {
        label: 'Selected: Pokémon',
        kicker: 'Pokémon • Whatnot',
        title: '@cardking47',
        copy: 'Slab auctions, premium cards, Vaulted Rarities, Secret Sprints and high-energy Pokémon collector shows.',
        tags: ['$1 Starts', 'Slab Auctions', 'Secret Sprints'],
        href: root.dataset.pokemonUrl,
        button: 'Open Pokémon Shows ↗'
      },
      tiktok: {
        label: 'Selected: TikTok Shop',
        kicker: 'TikTok Shop • Magic + Pokémon',
        title: '@cardking4747',
        copy: 'A fast-growing CK47 storefront focused heavily on MTG Surprise Sets and packs, with Pokémon products available in the shop too.',
        tags: ['14.4K Followers', '60.9K Sold', '4.9 ★'],
        href: root.dataset.tiktokUrl,
        button: 'Open TikTok Shop ↗'
      }
    };

    const tabs = root.querySelectorAll('[data-channel]');
    tabs.forEach(tab => {
      tab.addEventListener('click', () => {
        tabs.forEach(t => t.classList.remove('is-active'));
        tab.classList.add('is-active');
        const data = channelData[tab.dataset.channel];
        root.querySelector('[data-ck47-selected-label]').textContent = data.label;
        root.querySelector('[data-ck47-stage-kicker]').textContent = data.kicker;
        root.querySelector('[data-ck47-stage-title]').textContent = data.title;
        root.querySelector('[data-ck47-stage-copy]').textContent = data.copy;
        const tagWrap = root.querySelector('[data-ck47-stage-tags]');
        tagWrap.innerHTML = '';
        data.tags.forEach(tag => {
          const span = document.createElement('span');
          span.textContent = tag;
          tagWrap.appendChild(span);
        });
        const stageLink = root.querySelector('[data-ck47-stage-link]');
        stageLink.href = data.href;
        stageLink.textContent = data.button;
      });
    });

    const proofBar = root.querySelector('.ck47-proof-bar');
    if (proofBar && 'IntersectionObserver' in window) {
      const observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            proofBar.querySelectorAll('.ck47-count-up').forEach(animateCounter);
            observer.disconnect();
          }
        });
      }, { threshold: .35 });
      observer.observe(proofBar);
    } else if (proofBar) {
      proofBar.querySelectorAll('.ck47-count-up').forEach(animateCounter);
    }

    refreshLiveStatus(root);
    if ((root.dataset.liveEndpoint || '').trim()) {
      root._ck47LiveTimer = window.setInterval(() => refreshLiveStatus(root), 60000);
    }
  }

  document.querySelectorAll('[data-ck47-root]').forEach(init);

  document.addEventListener('shopify:section:load', event => {
    const root = event.target.querySelector?.('[data-ck47-root]') || (event.target.matches?.('[data-ck47-root]') ? event.target : null);
    if (root) init(root);
  });

  document.addEventListener('shopify:section:unload', event => {
    const root = event.target.querySelector?.('[data-ck47-root]') || (event.target.matches?.('[data-ck47-root]') ? event.target : null);
    if (root?._ck47LiveTimer) window.clearInterval(root._ck47LiveTimer);
    setTimeout(() => {
      if (!document.querySelector('[data-ck47-root][data-hide-theme-chrome="true"]')) {
        document.body.classList.remove('ck47-page-active');
      }
    }, 0);
  });
})();
